import { useSelector } from 'react-redux';
import { getOrder } from '../../../../Checkout/store/order/selectors';
import { useToggleable } from '../../../../hooks';
import { useCallback, useState } from 'react';
import { useIsCheckinAvailable } from '../../../../Checkout/hooks';
import {
	TravellerFieldEnum,
	useCheckinCreateOrderMutation,
	useCheckinRulesQuery,
	useGetCheckinOrderQuery,
	useResetOrderPaymentBlockMutation
} from '@websky/graphql';
import { useConfig } from '../../../../context';
import { captureErrorSentry, getCheckInUrl } from '../../../../utils';
import { useCheckinError } from '../../../../Modules/Checkin/hooks/useCheckinError';

export const useStartCheckIn = () => {
	const order = useSelector(getOrder);

	const { isOpen: isRulesModalOpen, open: openRulesModal, close: closeRulesModal } = useToggleable(false);
	const [checkinOrderId, setCheckinOrderId] = useState<string>();
	const [checkinRules, setCheckinRules] = useState<string | null>(null);
	const [checkinCreationInProgress, setCheckinCreationInProgress] = useState(false);

	const isCheckinAvailableForOrder = useIsCheckinAvailable(order);
	const { handleError: handleCheckinError } = useCheckinError();
	const [createCheckin] = useCheckinCreateOrderMutation();
	const [resetOrderPaymentBlock] = useResetOrderPaymentBlockMutation();
	const { refetch: getCheckinOrder } = useGetCheckinOrderQuery({ skip: true, fetchPolicy: 'no-cache' });
	const { refetch: getCheckinRules } = useCheckinRulesQuery({ skip: true, fetchPolicy: 'no-cache' });

	const {
		Engine: { checkInEnabled, checkInURL },
		global: {
			companyInfo: { iataCode },
			reserveEngines
		}
	} = useConfig();

	const isCheckinEnabled = checkInEnabled && !!checkInURL;
	const isCheckinAvailable = isCheckinAvailableForOrder && isCheckinEnabled;

	const goToCheckin = async () => {
		const traveller = order.travellers[0];

		const ticket = traveller?.tickets?.[0]?.number || order.locator;
		const lastName = traveller.values.find(value => value.type === TravellerFieldEnum.LastName)?.value;
		const orderId = order.id;

		if (reserveEngines?.redirectToWebsky1 && reserveEngines?.checkin) {
			window.open(getCheckInUrl(checkInURL, orderId, ticket, lastName, reserveEngines), '_blank');
		} else {
			setCheckinCreationInProgress(true);
			setCheckinOrderId('');
			setCheckinRules(null);

			let checkinOrderCreated = false;
			let checkinErrorHandled = false;

			try {
				const { data, errors } = await createCheckin({
					variables: {
						params: { aviaOrderId: order.id }
					}
				});

				if (errors?.length && handleCheckinError(errors)) {
					checkinErrorHandled = true;
				} else if (data?.CheckinCreateOrder) {
					const orderId = data.CheckinCreateOrder.id;

					let result = data.CheckinCreateOrder;

					if (data.CheckinCreateOrder.isBlockedForPayment) {
						await resetOrderPaymentBlock({ variables: { id: orderId } });
						const { data, errors } = await getCheckinOrder({ id: orderId });

						if (data.CheckinOrder && !errors) {
							result = data.CheckinOrder;
						}
					}

					setCheckinOrderId(result.id);
					checkinOrderCreated = true;

					if (checkInEnabled && checkInURL) {
						if (iataCode === 'N4') {
							window.open(getCheckInUrl(checkInURL, result.id), '_blank');
						} else {
							const { data: rulesData, errors: rulesErrors } = await getCheckinRules({
								id: result.id
							});
							const rules = rulesData?.CheckinRules;

							if (rulesErrors?.length || !rules) {
								captureErrorSentry(
									rulesErrors?.length
										? `CheckinRules query failed, skipping rules modal. orderId=${result.id}`
										: `CheckinRules is empty, skipping rules modal. orderId=${result.id}`,
									'warning'
								);
								window.open(getCheckInUrl(checkInURL, result.id), '_blank');
							} else {
								setCheckinRules(rules);
								openRulesModal();
							}
						}
					}
				}
			} catch (e) {
				if (handleCheckinError(e)) {
					checkinErrorHandled = true;
				} else {
					console.warn('Could not create check-in order. Proceed with passenger credentials.');
				}
			}

			setCheckinCreationInProgress(false);

			if (!checkinOrderCreated && !checkinErrorHandled) {
				window.open(getCheckInUrl(checkInURL, orderId, ticket, lastName), '_blank');
			}
		}
	};

	const acceptRules = useCallback(() => {
		if (checkinOrderId) {
			window.open(getCheckInUrl(checkInURL, checkinOrderId), '_blank');
		}

		closeRulesModal();
	}, [checkinOrderId, checkInURL, closeRulesModal]);

	return {
		loading: checkinCreationInProgress,
		isCheckinAvailable,
		isRulesModalOpen,
		closeRulesModal,
		checkinRules,
		goToCheckin,
		acceptRules
	};
};
